import {GetWishlistPageResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/service.types';
import {WishlistStreamItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/stream.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useTranslation} from '@aboutyou/i18n';
import {TrackWishlistPageView} from '@aboutyou/tracking';
import {
  mediaDesktopAndUp,
  mediaLargeScreens,
  mobileOnly,
} from '@aboutyou/ui-components';
import React, {useEffect, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {ProductStreamOffsetLimitPagination} from '../../../features/ProductStreamPagination/ProductStreamOffsetLimitPagination';
import {useSelectedDiscountColor} from '../../Campaigns/SelectedDiscounts/useSelectedDiscountColor';
import {useTotalSavings} from '../../Campaigns/TotalSavingsProvider';
import {filterSimpleAndAppInstallIncentiveCampaigns} from '../../Campaigns/discountsHelper';
import {useDiscounts} from '../../Campaigns/hooks/useDiscounts';
import {WishlistDiscountTabs} from '../WishlistDiscountTabs/WishlistDiscountTabs';
import {WishlistItemGrid} from '../WishlistItemGrid/WishlistItemGrid';
import {WishlistProductTile} from '../WishlistProductTile/WishlistProductTile';
import {WishlistSoldoutProductTile} from '../WishlistSoldoutProductTile/WishlistSoldoutProductTile';
import {
  DealsTabValue,
  useDealsTabsOpenedState,
} from '../hooks/useDealsTabsOpenedState';
import {WishlistEmpty} from './WishlistEmpty';
import {WishlistStreamProps} from './WishlistItemStream';
import {WishlistLoadingComponent} from './WishlistLoadingComponent';
import {WishlistSubscribeToNewsletterBanner} from './WishlistSubscribeToNewsletterBanner/WishlistSubscribeToNewsletterBanner';

const WISHLIST_CURRENTPAGE_STATE_KEY = 'WISHLIST_CURRENTPAGE_STATE_KEY';

export interface WishlistStreamViewProps
  extends GetWishlistPageResponse,
    WishlistStreamProps {
  isDataFetched?: boolean;
  isDataFetching: boolean;
  /**
   * Optional slot rendered between the discount tabs and the product grid.
   * Passed from the page level on mWeb so the banner request can be fired in
   * parallel with the stream (prefetch pattern) rather than after stream resolves
   * (which would create a data waterfall).
   */
  bannerSlot?: React.ReactNode;
}

const useWishlistSlicedItems = (items: WishlistStreamItem[]) => {
  const [currentPage, setCurrentPage] = useHistoryPageState(1, {
    uniqueKey: WISHLIST_CURRENTPAGE_STATE_KEY,
  });
  const currentItems = currentPage * 32;
  return {items: items.slice(0, currentItems - 1), setCurrentPage, currentPage};
};

export const WishlistPageStreamView: React.FC<WishlistStreamViewProps> = ({
  allItemsTab,
  discountedItemsTab,
  showConfirmOnDeleteItem,
  onClickAddToBasket,
  onClickSoldoutCta,
  gridColumns,
  smallAddToBasketCtaText,
  discountTotalSavings,
  ProductTileComponent,
  isDataFetched,
  isDataFetching,
  enableImageGalleryAnimation,
  bannerSlot,
}) => {
  const {parameter} = useDealsTabsOpenedState();
  const discounts = useDiscounts();
  const discountColor = useSelectedDiscountColor();
  const {t} = useTranslation();

  const items =
    parameter === DealsTabValue.deals &&
    discountedItemsTab?.items &&
    discounts.data?.discount
      ? discountedItemsTab.items
      : allItemsTab.items;

  const {
    items: slicedItems,
    setCurrentPage,
    currentPage,
  } = useWishlistSlicedItems(items);
  const {set: setTotalSavings} = useTotalSavings();

  const memoTabs = useMemo(() => {
    return [
      {
        label: allItemsTab.label,
        value: DealsTabValue.all,
        count: allItemsTab.productCount,
      },
      {
        label: discountedItemsTab?.label || t('Deal items'),
        value: DealsTabValue.deals,
        count: discountedItemsTab?.productCount || 0,
        backgroundColour: discountColor?.backgroundColor.hex,
        foregroundColour: discountColor?.foregroundColor.hex,
      },
    ];
  }, [allItemsTab.items, discountedItemsTab?.items]);

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setTotalSavings(discountTotalSavings);
  }, [discountTotalSavings]);

  // Single authoritative source for whether WishlistDiscountTabs render.
  // `bannerSlot` is always gated on this same predicate below, so the banner
  // placement can never disagree with tab presence regardless of when the page
  // passes a non-undefined slot.
  const shouldRenderDiscountTabs =
    discounts.data &&
    filterSimpleAndAppInstallIncentiveCampaigns(discounts.data.discount);
  const getFirstTileIndex = () => {
    const firstProductIndex = slicedItems.findIndex(
      item => item.type?.$case === 'deprecatedProduct',
    );
    return firstProductIndex === -1 ? 0 : firstProductIndex;
  };

  const firstTileIndex = useMemo(() => {
    return getFirstTileIndex();
  }, [slicedItems]);

  // Separate from `firstTileIndex` above (which checks a `deprecatedProduct`
  // case that no longer appears in the switch below, so it always falls back
  // to `0`) — this is used for the "Skip to results" skip-link anchor and
  // needs to land on an actual renderable product tile, not just index 0.
  const firstProductTileIndex = useMemo(
    () => slicedItems.findIndex(item => item.type?.$case === 'product'),
    [slicedItems],
  );

  if (!discountedItemsTab?.items.length && parameter === DealsTabValue.deals) {
    return (
      <>
        <TrackWishlistPageView
          itemStream={items}
          isDataFetching={isDataFetching}
          isDataFetched={isDataFetched}
        />
        {shouldRenderDiscountTabs && <WishlistDiscountTabs tabs={memoTabs} />}
        {bannerSlot}
        <WishlistItemGrid>
          <FullSpanItem withLargeVerticalPadding>
            <WishlistEmpty isDiscountedItemsTabEmpty />
          </FullSpanItem>
        </WishlistItemGrid>
      </>
    );
  }

  return (
    <>
      <TrackWishlistPageView
        itemStream={items}
        isDataFetching={isDataFetching}
        isDataFetched={isDataFetched}
      />
      {shouldRenderDiscountTabs && <WishlistDiscountTabs tabs={memoTabs} />}
      {bannerSlot}
      <WishlistItemGrid>
        {items.map((item, index) => {
          switch (item.type?.$case) {
            case 'product':
              return (
                <WishlistProductTile
                  id={
                    index === firstProductTileIndex
                      ? 'first-product-tile'
                      : undefined
                  }
                  data-skip-link={
                    index === firstProductTileIndex
                      ? t('Skip to results')
                      : undefined
                  }
                  smallAddToBasketCtaText={smallAddToBasketCtaText}
                  key={item.type.product.itemKey}
                  streamPosition={item.streamPosition}
                  tile={item.type.product.product}
                  showConfirmOnDeleteItem={showConfirmOnDeleteItem}
                  onClickAddToBasket={payload =>
                    onClickAddToBasket({...payload, badgesListKeys: []})
                  }
                  onSoldoutCtaClick={onClickSoldoutCta}
                  gridColumns={gridColumns}
                  ProductTileComponent={ProductTileComponent}
                  enableImageGalleryAnimation={
                    index === firstTileIndex && enableImageGalleryAnimation
                  }
                  trackingData={{
                    blockTypeAndId: 'product_stream',
                  }}
                />
              );
            case 'soldoutProduct':
              return (
                <WishlistSoldoutProductTile
                  key={item.type.soldoutProduct.itemKey}
                  tile={item.type.soldoutProduct.soldoutProduct}
                  itemKey={item.type.soldoutProduct.itemKey}
                  streamPosition={item.streamPosition}
                  onClickSoldoutCta={onClickSoldoutCta}
                  showConfirmOnDeleteItem={showConfirmOnDeleteItem}
                  gridColumns={gridColumns}
                />
              );
            case 'emptyWishlist':
              return (
                <>
                  {isDataFetching && (
                    <WishlistLoadingComponent onlyPlaceholder />
                  )}

                  {!isDataFetching && (
                    <FullSpanItem
                      withLargeVerticalPadding
                      key={item.streamPosition}
                    >
                      <WishlistEmpty />
                    </FullSpanItem>
                  )}
                </>
              );
            case 'newsletterBanner':
              return (
                <FullSpanItem>
                  <WishlistSubscribeToNewsletterBanner />
                </FullSpanItem>
              );
            default:
              return null;
          }
        })}
        <ProductStreamOffsetLimitPagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
          Wrapper={ListItem}
        />
      </WishlistItemGrid>
    </>
  );
};

const ListItem = styled.li``;

const loadMoreComponentDisplayConfig = {
  tilesBeforeFirstDisplay: 10000,
  tilesBeforeConsecutivelyDisplay: 10000,
};

const FullSpanItem = styled.li<{
  padded?: boolean;
  withLargeVerticalPadding?: boolean;
}>`
  margin: ${({padded}) => padded && '20px'} 0px;

  ${({withLargeVerticalPadding}) =>
    withLargeVerticalPadding &&
    css`
      margin: 50px 0px;
    `};

  grid-column: span 2;

  @media ${mediaDesktopAndUp} {
    grid-column: span 3;
  }

  @media ${mediaLargeScreens} {
    grid-column: span 4;
    ${({withLargeVerticalPadding}) =>
      withLargeVerticalPadding &&
      css`
        margin: 100px 0px;
      `};
  }

  ${mobileOnly(css`
    text-align: center;
  `)}
`;
